// Mission Board — Issues framed as mission drops, unlocked after onboarding.
// Route: /missions/:token (same secure participant token as acceptance/onboarding).
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Lock } from "lucide-react";
import { getAcceptance, errText } from "@/lib/api";
import { ISSUES } from "@/data/site";
import { Reveal } from "@/components/Reveal";
import { Kicker } from "@/components/Section";

const missionStatus = (s) => {
  if (/paid|sponsor/i.test(s)) return { label: "ACTIVE OP", cls: "border-gold bg-gold/10 text-goldlight" };
  if (/portfolio/i.test(s)) return { label: "BRIEFING", cls: "border-silver/50 bg-silver/5 text-silver" };
  return { label: "STANDBY", cls: "border-silver/30 text-silver/60" };
};

const norm = (s) => (s || "").trim().toLowerCase();

const MissionCard = ({ issue, i, lane, deployed }) => {
  const st = missionStatus(issue.status);
  const laneDeployed = !deployed && norm(lane) && (
    issue.disciplines.some((d) => norm(d) === norm(lane)) ||
    (issue.breakdown || []).some((b) => norm(b.lane) === norm(lane))
  );
  return (
    <Reveal delay={i * 0.08}>
      <Link to={`/issues/${issue.slug}`} data-testid={`mission-card-${issue.number}`} className="group block h-full">
        <motion.div whileHover={{ y: -4 }} className={`flex h-full flex-col overflow-hidden gold-frame bg-charcoal transition-shadow duration-300 group-hover:shadow-[0_0_35px_rgba(201,162,39,0.18)] ${deployed ? "shadow-[0_0_30px_rgba(201,162,39,0.22)]" : ""}`}>
          <div className="panel-mono relative h-44 overflow-hidden">
            <img src={issue.cover} alt={issue.title} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-onyx/80 to-transparent" />
            <span className="absolute left-3 top-3 border border-gold/50 bg-onyx/85 px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-gold">
              Mission {issue.number}
            </span>
            <span className={`absolute right-3 top-3 border bg-onyx/85 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.2em] ${st.cls}`}>
              {st.label}
            </span>
          </div>
          <div className="flex flex-1 flex-col border-t border-gold/25 p-5">
            <h3 className="font-display text-2xl uppercase leading-none tracking-tight text-cream">{issue.title}</h3>
            <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.25em] text-silver/50">Objective</p>
            <p className="mt-1 flex-1 font-body text-sm leading-relaxed text-silver/80">{issue.summary}</p>
            {deployed && (
              <p className="mt-3 inline-flex items-center gap-2 border border-gold bg-gradient-to-b from-goldlight/20 to-gold/20 px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-goldlight" data-testid={`deployed-${issue.number}`}>
                <span className="h-1.5 w-1.5 animate-pulse rotate-45 bg-gold" /> You're deployed on this mission
              </p>
            )}
            {laneDeployed && (
              <p className="mt-3 inline-flex items-center gap-2 border border-gold/40 bg-onyx px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-goldlight" data-testid={`lane-deployed-${issue.number}`}>
                <span className="h-1.5 w-1.5 rotate-45 bg-gold" /> Your lane deploys on this mission
              </p>
            )}
            <span className="mt-4 inline-flex items-center gap-2 font-head text-xs font-extrabold uppercase tracking-[0.2em] text-goldlight">
              Open Mission File <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
            </span>
          </div>
        </motion.div>
      </Link>
    </Reveal>
  );
};

export default function Missions() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getAcceptance(token).then(setData).catch((e) => setError(errText(e)));
  }, [token]);

  if (error) {
    return (
      <div className="grain-dark bg-onyx px-4 py-32 text-center text-cream" data-testid="missions-error">
        <h1 className="font-display text-4xl uppercase tracking-tight text-metallic sm:text-6xl">Access denied</h1>
        <p className="mx-auto mt-4 max-w-md font-body text-silver/75">{error}</p>
        <Link to="/" className="mt-8 inline-block border border-silver/40 px-8 py-3.5 font-head text-sm font-extrabold uppercase tracking-[0.2em] text-silver transition-colors hover:border-gold hover:text-goldlight">Return to the Village</Link>
      </div>
    );
  }
  if (!data) return <div className="bg-onyx py-40 text-center font-head font-bold uppercase text-silver/50">Opening the board...</div>;

  const unlocked = data.onboarding_status === "complete";
  const name = data.name || "Founding Creative";

  if (!unlocked) {
    return (
      <div className="grain-dark relative bg-onyx px-4 py-32 text-center text-cream" data-testid="missions-locked">
        <div className="halftone-gold pointer-events-none absolute inset-0 opacity-[0.04]" />
        <div className="relative">
          <Lock className="mx-auto h-10 w-10 text-gold" />
          <h1 className="mt-6 font-display text-4xl uppercase tracking-tight text-metallic sm:text-6xl">Mission board locked</h1>
          <p className="mx-auto mt-4 max-w-md font-body text-silver/75">
            Complete your briefing to unlock your assignments{name.split(" ")[0].length > 1 ? `, ${name.split(" ")[0]}` : ""}.
            The Architect doesn't hand out missions to unconfirmed recruits.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            {data.place_accepted ? (
              <Link to={`/onboarding/${token}`} data-testid="missions-unlock-btn" className="btn-sweep border border-gold bg-gradient-to-b from-goldlight to-gold px-8 py-4 font-head text-sm font-extrabold uppercase tracking-[0.2em] text-onyx shadow-[0_0_30px_rgba(201,162,39,0.3)] transition-transform hover:-translate-y-0.5">Complete Your Briefing</Link>
            ) : (
              <Link to={`/acceptance/${token}`} data-testid="missions-accept-btn" className="btn-sweep border border-gold bg-gradient-to-b from-goldlight to-gold px-8 py-4 font-head text-sm font-extrabold uppercase tracking-[0.2em] text-onyx shadow-[0_0_30px_rgba(201,162,39,0.3)] transition-transform hover:-translate-y-0.5">Open Your Invitation</Link>
            )}
            <Link to="/" className="border border-silver/40 px-8 py-4 font-head text-sm font-extrabold uppercase tracking-[0.2em] text-silver transition-colors hover:border-gold hover:text-goldlight">Return to the Village</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-onyx text-cream" data-testid="missions-page">
      <section className="grain-dark relative overflow-hidden border-b border-gold/25 py-16 sm:py-20">
        <div className="pointer-events-none absolute left-1/3 top-1/2 h-[70vmin] w-[70vmin] -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ background: "radial-gradient(circle, rgba(201,162,39,0.1) 0%, transparent 60%)" }} />
        <div className="halftone-gold pointer-events-none absolute inset-0 opacity-[0.04]" />
        <div className="relative mx-auto max-w-[1200px] px-4 sm:px-6">
          <Kicker>Assigned by The Architect</Kicker>
          <h1 className="mt-5 font-display text-5xl uppercase leading-[0.85] tracking-tight text-metallic sm:text-7xl">Mission Board</h1>
          <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="missions-member-chip">
            <span className="border border-gold/40 bg-charcoal px-3 py-1.5 font-head text-sm font-extrabold uppercase tracking-wide text-cream">{name}</span>
            <span className="border border-silver/30 bg-charcoal px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-silver">{data.primary_lane || "Creative Cohort Member"}</span>
            <span className="border border-gold/40 bg-onyx px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-gold">{data.member_number || "Founding Member"}</span>
            <span className="border border-gold/25 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-goldlight/80">Team Status: Founding Cohort Member</span>
          </div>
          <p className="mt-5 max-w-xl font-body text-silver/75">
            Every Issue is a mission — one shared brief executed by a multidisciplinary crew. Open a mission file to study the objective, the crew, and the breakdown.
          </p>
        </div>
      </section>

      <section className="grain-dark bg-onyx py-16 sm:py-20">
        <div className="relative mx-auto max-w-[1200px] px-4 sm:px-6">
          <p className="font-mono text-[9px] uppercase tracking-[0.35em] text-silver/40">BLV / Operations Ledger · Founding Cohort {data.cohort_year}</p>
          <div className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="missions-grid">
            {ISSUES.map((issue, i) => (
              <MissionCard key={issue.slug} issue={issue} i={i} lane={data.primary_lane} deployed={(data.mission_assignments || []).includes(issue.number)} />
            ))}
            <Reveal delay={ISSUES.length * 0.08}>
              <div className="flex h-full min-h-[320px] flex-col items-center justify-center border border-dashed border-gold/40 bg-charcoal/50 p-8 text-center" data-testid="next-mission-card">
                <span className="font-display text-5xl text-gold-metallic">004</span>
                <p className="mt-3 font-head text-base font-extrabold uppercase tracking-tight text-cream">Next Mission Loading</p>
                <p className="mt-2 font-body text-sm text-silver/60">New mission drops are assigned by The Architect each cohort. Watch your inbox.</p>
              </div>
            </Reveal>
          </div>
          <p className="mt-10 font-body text-xs text-silver/45">
            Mission assignments depend on lane, availability, and project needs — appearing on the board doesn't guarantee a specific assignment or paid role. Compensation is always disclosed per project.
          </p>
        </div>
      </section>
    </div>
  );
}
